//React
import { FunctionComponent, useEffect } from "react";

//Models
import { Result } from "../../models/result";

//App Hooks
import {
    useAppDispatch,
    useAppSelector,
} from "../../../system/redux/hooks/redux-store-hooks";

//Actions
import {
    clearSelectedResult,
    setSelectedResult,
} from "../../redux/slices/result-list-slice";

//Components
import ResultImage from "./result-image";

//Util
import { selectIsMobile } from "../../../system/redux/selectors/select-is-mobile";

interface ResultContentProps {
    result: Result;
    isInInfoWindow: boolean;
}

const formatPrice = (price: number, decimals: number): string => {
    if (typeof price !== 'number' || isNaN(price)) {
        return '';
    }
    try {
      const formatter = new Intl.NumberFormat("en", {
        style: 'currency',
        currency: "USD",
        maximumFractionDigits: decimals,
        minimumFractionDigits: decimals,
      });

      return formatter.format(price);
    } catch (error) {
      console.error("Error formatting currency:", error);
      return `${price.toFixed(2)}`; // Fallback
    }
}

const formatSqFt = (sqft: number): string => {
    return Intl.NumberFormat('en-US').format(sqft);
}

/** Displays the result card for the given community result */
const ResultCardQmi: FunctionComponent<ResultContentProps> = (props) => {
    //Properties
    const { result, isInInfoWindow } = props;

    //Store Dispatch
    const dispatch = useAppDispatch();

    const isMobile = useAppSelector(selectIsMobile);

    const nowPrice = result?.prices[0];
    const wasPrice = result?.originalprice;
    const showWasPrice = wasPrice == 0 ? false : true;

    const mpcBaseUrl = "https://mpc.drhorton.com/";

    //State Values
    const sampleConfigId = "3264343";
    const ariaLabel = "Original price was " + formatPrice(wasPrice, 0);

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, configId: number) => {
        // prevent browser from following <a> tag link
        e.preventDefault();

        // create the iframe URL
        const completeUrl = mpcBaseUrl + configId;

        // get dialog HTML elements
        const dialogElement = document.getElementById("mpcModal") as HTMLDialogElement;
        const mpcCloseButton = document.getElementById("mpc-iframe-close-button");
        const mpcIframe = document.getElementById("mpc-iframe");

        // set modal class
        if (dialogElement) {
            var cleanId = result?.uniqueId.replaceAll("-", "").toUpperCase();
            var mpcClass = "B-" + cleanId;
            dialogElement.classList.add(mpcClass);
        }

        // set iframe source
        if (mpcIframe) {
            // @ts-ignore
            mpcIframe.src = completeUrl;
        }
        
        // set modal close functionality
        if (dialogElement && mpcCloseButton) {
            // @ts-ignore
            mpcCloseButton.addEventListener("click", () => { dialogElement.close() });
             // @ts-ignore
            dialogElement.addEventListener("click", () => { dialogElement.close() });
        }
        
        // set modal open functionality
        if (dialogElement) {
             // @ts-ignore
            dialogElement.showModal();
        }
    }

    //JSX
    return (
        <div
			className="coveo-list-layout CoveoResult"
			role="listitem"
			key={result.uniqueId}
			//Only use the hover events in desktop mode
			onMouseEnter={() => !isMobile && dispatch(setSelectedResult(result))}
			onMouseLeave={() => !isMobile && dispatch(clearSelectedResult())}
            aria-label="Home of the week card"
		>
            <a href={result?.url + "&video=hotw"} aria-label="Watch video about homes of the week" className="hotw-wrapper">
                <img className="hotw-coin" src="/-/media/hotw_coin_flat.svg" />
                <div className="hotw-coin-shadow"></div>
            </a>
            <a className="CoveoResultLink available-home-card" href={result?.url} >
                <div className="card card-hotw">
                    <ResultImage result={result} />
                    <div className="card-content">
                        <h2>{formatPrice(nowPrice, 0)}
                            {showWasPrice && 
                                <span className="wasPricingCard"><del aria-label={ariaLabel}>was {formatPrice(wasPrice, 0)}</del></span>
                            }
                            
                            {/* to test locally without having to update SC data
                             <p className="subtitle-price">
                                    <object>
                                        <a onClick={(e) => handleClick(e, sampleConfigId)} className="estimatedLink">
                                            <div className="qmiMonthlyPrice">
                                                {formatPrice(result?.monthlyPayment, 2)} / monthly*
                                                <span className="est-payment">*see total est. payment details</span>
                                            </div>
                                        </a>
                                    </object>
                                </p>*/}
                            
                            {result?.usempc && result?.monthlyPayment > 0 &&
                                <p className="subtitle-price">
                                    <object>
                                        <a onClick={(e) => handleClick(e, result?.mpcconfigid)} className="estimatedLink">
                                            <div className="qmiMonthlyPrice">
                                                {formatPrice(result?.monthlyPayment, 2)} / monthly*
                                                <span className="est-payment">*see total est. payment details</span>
                                            </div>
                                        </a>
                                    </object>
                                </p>
                            }
                        </h2>
                        <h3>{result?.address}</h3>
                        <p>
                            {result?.bedrooms[0]}<span> Bed </span> 
                            <span className="vert-divider">|</span>
                            {result?.bathrooms[0]}<span> Bath</span> 
                            <span className="vert-divider">|</span>
                            {result?.garages[0]}<span> Garage</span>
                        </p>
                        <p>
                            {result?.stories[0]}<span> Story</span> 
                            <span className="vert-divider">|</span>
                            {formatSqFt(result?.size[0])}<span> Sq. Ft.</span> 
                            <span className="vert-divider">|</span> 
                            <span className="lot-number">Lot {result?.lot}</span>
                        </p>
                    </div>
                </div>
            </a>
        </div>
    );
};

export default ResultCardQmi;
